import React, { useEffect, useState, useContext } from "react";
import { TENANT_PLATFORM } from '@hiver/bifrost-js';
import EmailProviderOption from "./EmailProviderOption";
import {
    Label,
    StartedWrapper,
    ImageWrapper,
    SecondWrapper,
    Title,
    LogoWrapper,
    LoginButton,
    StyledInput,
    ErrorMessage,
    FlexBox,
    LogoImage,
    EmailLabelText,
    EmailProviderOptionsWrapper,
    EmailProviderWrapper,
    SelectLoginButtonTextWrapper
} from "./styled.js";
import loginBanner from "../../images/loginBanner.svg";
import {
    LOG_IN,
    EMAIL_PLACEHOLDER,
    LOG_IN_DESCRIPTION,
    WELCOME,
    DIFF_EMAIL_ERROR,
    NO_ACCESS_ERROR,
    LOGO_IMG,
    selectEmailProviderText,
    emailLabel,
    EMAIL_SELECTOR_PROVIDERS
} from "./constants";
import { validateEmail } from "../../Constants/utility.js";
import { KBContext, AutherContext, ToastContext } from "../../Context";
import { getLogo, initiateLoginHOT } from "./utils.js";
import useNavigateWithSearch from "../../Context/KBContext/hooks/useNavigateWithSearch.js";

export default function LoginPage() {
    const { setShowToast } = useContext(ToastContext);
    const { data : kbData } = useContext(KBContext);
    const [emailAddress, setEmailAddress] = useState("");
    const [selectedEmailProvider, setSelectedEmailProvider] = useState(TENANT_PLATFORM.GMAIL);
    const [showLoginError, setShowLoginError] = useState(false);
    const {
      setRefetchToken,
      setRedirectPage,
      setEmail,
      showError: showDifferentEmailError,
      hasPermission,
      loginInitiated,
      setLoginInitiated
    } = useContext(AutherContext);
    const navigate = useNavigateWithSearch();

    const handleLoginButton = () => {
        if (selectedEmailProvider === TENANT_PLATFORM.HIVERWEB) {
            return initiateLoginHOT({ email: emailAddress, onError: setShowToast });
        }
        setRefetchToken(true);
        setRedirectPage(true);
        setEmail(emailAddress);
        setLoginInitiated(false);
    };

    const handleChangeEmailProvider = (emailProviderName) => {
        if(selectedEmailProvider !== emailProviderName) {
            setSelectedEmailProvider(emailProviderName);
        }
    };

    useEffect(() => {
        setShowLoginError(loginInitiated && (showDifferentEmailError || !hasPermission));
    }, [hasPermission, loginInitiated, showDifferentEmailError]);

    useEffect(() => {
        const { isInternal } = kbData;
        if (isInternal !== undefined) {
            if ((isInternal && hasPermission) || !isInternal) {
                setLoginInitiated(false);
                navigate("/");
            }
        }
    }, [kbData, hasPermission, setLoginInitiated, navigate]);

    const handleOnChange = (e) => {
        e?.stopPropagation();
        const { value } = e.target || {};
        setEmailAddress(value ?? "");
    };

    const isDisabled = !validateEmail(emailAddress);

    const handleKeyPress = (e) => {
        e?.stopPropagation();
        if (e.key === "Enter" && !isDisabled) {
            handleLoginButton();
        }
    };

    let logo = getLogo(kbData);
    logo = logo ? LOGO_IMG.replaceAll('{{LOGO}}', logo) : false;

    return (
        <StartedWrapper>
            <ImageWrapper>
                <img alt='getStartedScreen' src={loginBanner} />
            </ImageWrapper>
            <SecondWrapper>
                {
                    logo &&
                    <LogoWrapper>
                        <LogoImage alt="Logo" src={logo} />
                    </LogoWrapper>
                }
                <Title>{WELCOME}</Title>
                <Label>{LOG_IN_DESCRIPTION}</Label>
                <EmailProviderWrapper>
                    <SelectLoginButtonTextWrapper>{selectEmailProviderText}</SelectLoginButtonTextWrapper>
                    <EmailProviderOptionsWrapper>
                        {EMAIL_SELECTOR_PROVIDERS.map((provider) => (
                            <EmailProviderOption
                            key={provider.TENANT_ID}
                            provider={provider}
                            isSelected={selectedEmailProvider === provider.TENANT_ID}
                            onSelect={handleChangeEmailProvider}
                            />
                        ))}
                    </EmailProviderOptionsWrapper>
                </EmailProviderWrapper>
                <EmailLabelText>{emailLabel}</EmailLabelText>
                <StyledInput
                    className={showLoginError ? 'error' : ''}
                    onKeyDown={handleKeyPress}
                    placeholder={EMAIL_PLACEHOLDER}
                    value={emailAddress}
                    onChange={handleOnChange}
                />
                {showLoginError && (
                    <FlexBox>
                        <ErrorMessage>
                            {showDifferentEmailError ? DIFF_EMAIL_ERROR : NO_ACCESS_ERROR}
                        </ErrorMessage>
                    </FlexBox>
                )}
                <LoginButton
                    className={isDisabled ? 'disabled' : ''}
                    aria-label={LOG_IN}
                    onClick={handleLoginButton}
                >
                    {LOG_IN}
                </LoginButton>
            </SecondWrapper>
        </StartedWrapper>
    );
}
